<script setup>
import { computed, onMounted, onUnmounted, ref, useTemplateRef } from 'vue'
import { useRouter, useRoute } from 'vue-router'

import { usePopover, useClickOutside } from '@/composables/usePopover'
import { useSidePanel } from '@/composables/useSidePanel'
import { useNotificationsStore } from '@/stores/Notifications'

const router = useRouter()
const route = useRoute()
const notificationStore = useNotificationsStore()
const { open } = useSidePanel()

const { isOpen, toggle: togglePopover, close } = usePopover()

const menuRef = useTemplateRef('menuRef')

const isMobile = ref(false)
const checkMobile = () => {
  isMobile.value = window.innerWidth < 640 // sm breakpoint
}
onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
})
onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
})

const buttonText = computed(() => (isMobile.value ? undefined : 'Notificações'))

const toggle = () => {
  togglePopover()
  if (isOpen.value) {
    notificationStore.hasNewNotifications = false
  }
}

const goToSettings = () => {
  if (route.path != '/notifications') {
    router.push('/notifications/settings')
  }
  toggle()
}

const goToListNotifications = () => {
  router.push('/notifications')
  toggle()
}

const openNotification = notification => {
  open('notification-detail', { notification })
  toggle()
}

const notificationToShow = computed(() => notificationStore.getNotifications.slice(0, 5))

useClickOutside(menuRef, close)
</script>

<template>
  <div class="relative">
    <ImoButton
      variant="tertiary"
      :icon="notificationStore.hasNewNotifications ? 'notification-active' : 'notification'"
      iconPosition="left"
      :text="buttonText"
      @click.stop="toggle"
      ref="buttonRef"
      size="sm"
    />

    <!-- Backdrop para mobile -->
    <Transition
      enter-active-class="transition-opacity duration-300 ease-out"
      leave-active-class="transition-opacity duration-200 ease-in"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="isOpen" class="fixed inset-0 z-40 bg-black/50 md:hidden" @click="toggle" />
    </Transition>

    <Transition
      enter-active-class="transition-transform duration-300 ease-out md:transition-opacity"
      leave-active-class="transition-transform duration-200 ease-in md:transition-opacity"
      enter-from-class="translate-y-full md:translate-y-0 md:opacity-0"
      enter-to-class="translate-y-0 md:opacity-100"
      leave-from-class="translate-y-0 md:opacity-100"
      leave-to-class="translate-y-full md:translate-y-0 md:opacity-0"
    >
      <div
        v-if="isOpen"
        ref="menuRef"
        class="border-neutral-20 fixed right-0 bottom-0 left-0 z-50 flex w-full flex-col gap-6 rounded-t-3xl border bg-white px-4 py-6 drop-shadow-lg md:absolute md:top-[43px] md:right-auto md:bottom-auto md:left-0 md:w-[463px] md:rounded-lg"
      >
        <div class="flex w-full flex-row justify-between px-4 py-0.5">
          <h2 class="text-primary-90 text-[22px]">Notificações</h2>
          <ImoIcon name="setting-2" class="cursor-pointer" @click="goToSettings" />
        </div>
        <ul class="flex max-h-[400px] flex-col gap-2 overflow-y-auto">
          <li
            v-for="notification in notificationToShow"
            class="border-neutral-10 hover:bg-neutral-20 flex cursor-pointer items-center justify-between rounded-xl border-b p-2"
            @click.stop="openNotification(notification)"
          >
            <div class="flex flex-row gap-2">
              <div class="pt-1.5">
                <ImoIcon name="record" :size="12" class="text-info-70" fill="text-info-70" />
              </div>
              <div class="flex flex-col">
                <h4 class="font-bold">{{ notification.title }}</h4>
                <p class="text-justify text-sm text-ellipsis">{{ notification.description }}</p>
              </div>
            </div>
          </li>
        </ul>
        <div>
          <ImoTextButton
            text="Ver todas as notificações"
            variant="secondary"
            icon="arrow-right-02"
            @click.stop="goToListNotifications"
          />
        </div>
      </div>
    </Transition>
  </div>
</template>
